import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  IconButton,
  Divider,
  Link,
  RadioGroup,
  Radio,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import BarcodeLabel from './BarcodeLabel';
import SizedLabelBox from './SizedLabelBox';
import useLabelPrint from '../hooks/useLabelPrint';

const STORAGE_KEY = 'resellerpro_label_preferences';
const QUAILHQ_STORAGE_KEY = 'resellerpro_label_preferences_quailhq';

const VALID_FORMATS = ['svg', 'png', 'pdf'];

const LABEL_SIZE_PRESETS = [
  { id: '2x1', label: '2" × 1" (small SKU)', widthMm: 50.8, heightMm: 25.4, shape: 'rect', rotation: 0 },
  { id: '1x2.125', label: '1" × 2⅛" (address)', widthMm: 25.4, heightMm: 53.975, shape: 'rect', rotation: 90 },
  { id: 'niimbot-50x30', label: '50 × 30 mm (Niimbot)', widthMm: 50, heightMm: 30, shape: 'rect', rotation: 0 },
  { id: '2round', label: '2" round sticker', widthMm: 50.8, heightMm: 50.8, shape: 'circle', rotation: 0 },
  { id: '4x6', label: '4" × 6" (shipping)', widthMm: 101.6, heightMm: 152.4, shape: 'rect', rotation: 0 },
];
const CUSTOM_PRESET_ID = 'custom';
const VALID_SHAPES = ['rect', 'circle'];
const VALID_ROTATIONS = [0, 90];

const DEFAULT_PREFERENCES = {
  showTitle: true,
  showPrice: true,
  showChannelName: false,
  showLocation: false,
  showCustomText: false,
  customText: '',
  format: 'svg',
  labelWidthMm: 50.8,
  labelHeightMm: 25.4,
  labelShape: 'rect',
  labelRotation: 0,
  boldAllFields: false,
};

const QUAILHQ_DEFAULT_PREFERENCES = {
  showTitle: false,
  showPrice: true,
  showChannelName: false,
  showLocation: true,
  showCustomText: false,
  customText: '',
  format: 'svg',
  labelWidthMm: 50.8,
  labelHeightMm: 25.4,
  labelShape: 'rect',
  labelRotation: 0,
  boldAllFields: false,
};

const matchPresetId = (widthMm, heightMm, shape, rotation) => {
  const found = LABEL_SIZE_PRESETS.find(
    (p) =>
      Math.abs(p.widthMm - widthMm) < 0.05 &&
      Math.abs(p.heightMm - heightMm) < 0.05 &&
      p.shape === shape &&
      p.rotation === rotation
  );
  return found ? found.id : CUSTOM_PRESET_ID;
};

const sanitizeDim = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return n;
};

const loadPreferences = (isQuailHQ = false) => {
  const key = isQuailHQ ? QUAILHQ_STORAGE_KEY : STORAGE_KEY;
  const defaults = isQuailHQ ? QUAILHQ_DEFAULT_PREFERENCES : DEFAULT_PREFERENCES;
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const merged = { ...defaults, ...JSON.parse(stored) };
      if (!VALID_FORMATS.includes(merged.format)) {
        merged.format = 'svg';
      }
      merged.labelWidthMm = sanitizeDim(merged.labelWidthMm, defaults.labelWidthMm);
      merged.labelHeightMm = sanitizeDim(merged.labelHeightMm, defaults.labelHeightMm);
      if (!VALID_SHAPES.includes(merged.labelShape)) {
        merged.labelShape = defaults.labelShape;
      }
      if (!VALID_ROTATIONS.includes(Number(merged.labelRotation))) {
        merged.labelRotation = defaults.labelRotation;
      } else {
        merged.labelRotation = Number(merged.labelRotation);
      }
      return merged;
    }
  } catch {
    // ignore parse errors
  }
  return { ...defaults };
};

const PrintLabelDialog = ({ open, onClose, items = [] }) => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const contentRef = useRef(null);

  const { print: handlePrint, error: printError, clearError } = useLabelPrint({
    contentRef,
    format: preferences.format,
    labelWidthMm: preferences.labelWidthMm,
    labelHeightMm: preferences.labelHeightMm,
  });

  const labelRenderer = preferences.format === 'svg' ? 'svg' : 'img';
  const currentPresetId = matchPresetId(
    preferences.labelWidthMm,
    preferences.labelHeightMm,
    preferences.labelShape,
    preferences.labelRotation
  );

  const hasQuailHQItems = items.some(item => item.isQuailHQ);

  // Load preferences when dialog opens
  useEffect(() => {
    if (open) {
      setPreferences(loadPreferences(hasQuailHQItems));
    }
  }, [open, hasQuailHQItems]);

  // Save preferences to localStorage on change
  const savePreferences = useCallback((newPrefs) => {
    setPreferences(newPrefs);
    const key = hasQuailHQItems ? QUAILHQ_STORAGE_KEY : STORAGE_KEY;
    try {
      localStorage.setItem(key, JSON.stringify(newPrefs));
    } catch {
      // ignore storage errors
    }
  }, [hasQuailHQItems]);

  const handleToggle = (field) => (e) => {
    const newPrefs = { ...preferences, [field]: e.target.checked };
    // Clear customText when toggling off
    if (field === 'showCustomText' && !e.target.checked) {
      newPrefs.customText = '';
    }
    savePreferences(newPrefs);
  };

  const handleCustomTextChange = (e) => {
    savePreferences({ ...preferences, customText: e.target.value });
  };

  const handleFormatChange = (e) => {
    const next = e.target.value;
    if (!VALID_FORMATS.includes(next)) return;
    clearError();
    savePreferences({ ...preferences, format: next });
  };

  const handlePresetChange = (e) => {
    const id = e.target.value;
    if (id === CUSTOM_PRESET_ID) {
      // Keep current dimensions; user can now edit them.
      return;
    }
    const preset = LABEL_SIZE_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    savePreferences({
      ...preferences,
      labelWidthMm: preset.widthMm,
      labelHeightMm: preset.heightMm,
      labelShape: preset.shape,
      labelRotation: preset.rotation,
    });
  };

  const handleRotationToggle = (e) => {
    savePreferences({ ...preferences, labelRotation: e.target.checked ? 90 : 0 });
  };

  const handleDimChange = (field) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      savePreferences({ ...preferences, [field]: '' });
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return;
    savePreferences({ ...preferences, [field]: n });
  };

  const handleResetDefaults = () => {
    const defaults = hasQuailHQItems ? QUAILHQ_DEFAULT_PREFERENCES : DEFAULT_PREFERENCES;
    savePreferences({ ...defaults });
    const key = hasQuailHQItems ? QUAILHQ_STORAGE_KEY : STORAGE_KEY;
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  };

  if (!open || items.length === 0) return null;

  const hasNonBarcodeItems = items.some(item => item.barcodeRequired === false);
  const dialogTitle = hasNonBarcodeItems
    ? `Print Label${items.length > 1 ? 's' : ''}`
    : `Print Barcode Label${items.length > 1 ? 's' : ''}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {dialogTitle}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {printError && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
            {printError}
          </Alert>
        )}
        <Grid
          container
          spacing={3}
          alignItems="flex-start"
          sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
        >
          {/* Preferences Panel */}
          <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Label Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.showTitle}
                  onChange={handleToggle('showTitle')}
                  size="small"
                />
              }
              label="Show Title"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.showPrice}
                  onChange={handleToggle('showPrice')}
                  size="small"
                />
              }
              label="Show Price"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.showChannelName}
                  onChange={handleToggle('showChannelName')}
                  size="small"
                />
              }
              label="Show Channel Name"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.showLocation}
                  onChange={handleToggle('showLocation')}
                  size="small"
                />
              }
              label="Show Location"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.showCustomText}
                  onChange={handleToggle('showCustomText')}
                  size="small"
                />
              }
              label="Show Custom Text"
            />
            {preferences.showCustomText && (
              <TextField
                fullWidth
                size="small"
                label="Custom Text"
                value={preferences.customText}
                onChange={handleCustomTextChange}
                placeholder="Enter custom text"
                sx={{ mt: 1 }}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={!!preferences.boldAllFields}
                  onChange={handleToggle('boldAllFields')}
                  size="small"
                />
              }
              label="Bold All Fields"
            />

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Print Format
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Some printers may not print correctly with SVG, please consult your manual and choose the format your printer supports.
            </Typography>
            <RadioGroup
              value={preferences.format}
              onChange={handleFormatChange}
              sx={{ '& .MuiFormControlLabel-root': { mr: 0 } }}
            >
              <FormControlLabel
                value="svg"
                control={<Radio size="small" />}
                label={<Typography variant="body2">SVG (default)</Typography>}
              />
              <FormControlLabel
                value="png"
                control={<Radio size="small" />}
                label={<Typography variant="body2">PNG (raster image)</Typography>}
              />
              <FormControlLabel
                value="pdf"
                control={<Radio size="small" />}
                label={<Typography variant="body2">PDF (thermal printers)</Typography>}
              />
            </RadioGroup>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Label Size
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <InputLabel id="label-size-preset-label">Preset</InputLabel>
              <Select
                labelId="label-size-preset-label"
                label="Preset"
                value={currentPresetId}
                onChange={handlePresetChange}
              >
                {LABEL_SIZE_PRESETS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
                <MenuItem value={CUSTOM_PRESET_ID}>Custom…</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                label="Width (mm)"
                type="number"
                size="small"
                value={preferences.labelWidthMm}
                onChange={handleDimChange('labelWidthMm')}
                inputProps={{ min: 1, step: 0.1 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Height (mm)"
                type="number"
                size="small"
                value={preferences.labelHeightMm}
                onChange={handleDimChange('labelHeightMm')}
                inputProps={{ min: 1, step: 0.1 }}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.labelRotation === 90}
                  onChange={handleRotationToggle}
                  size="small"
                />
              }
              label={<Typography variant="body2">Rotate content 90°</Typography>}
              sx={{ mt: 0.5 }}
            />

            <Box sx={{ mt: 1.5 }}>
              <Link
                component="button"
                variant="caption"
                onClick={handleResetDefaults}
                underline="hover"
              >
                Reset to Defaults
              </Link>
            </Box>
          </Grid>

          {/* Divider */}
          <Grid size={{ xs: 12, sm: 'auto' }} sx={{ display: { xs: 'block', sm: 'flex' } }}>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
          </Grid>

          {/* Label Preview */}
          <Grid size={{ xs: 12, sm: 'grow' }} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Preview
            </Typography>
            <Box
              ref={contentRef}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: items.length === 1 ? 'center' : 'flex-start',
                '@media print': {
                  display: 'contents',
                },
              }}
            >
              {items.map((item, idx) => (
                <Box
                  key={item.itemNumber || idx}
                  sx={{
                    display: 'inline-block',
                    '@media print': {
                      display: 'block',
                      pageBreakAfter: idx < items.length - 1 ? 'always' : 'avoid',
                      pageBreakInside: 'avoid',
                    },
                  }}
                >
                  <SizedLabelBox
                    widthMm={Number(preferences.labelWidthMm) || DEFAULT_PREFERENCES.labelWidthMm}
                    heightMm={Number(preferences.labelHeightMm) || DEFAULT_PREFERENCES.labelHeightMm}
                    shape={preferences.labelShape}
                    rotation={preferences.labelRotation}
                  >
                    <BarcodeLabel
                      itemNumber={item.itemNumber}
                      barcodeValue={item.barcodeValue}
                      format={item.barcodeFormat}
                      itemTitle={item.itemTitle}
                      listPrice={item.listPrice}
                      channelName={item.channelName}
                      location={item.location}
                      customText={preferences.customText}
                      showBarcode={item.barcodeRequired !== false}
                      showTitle={preferences.showTitle}
                      showPrice={preferences.showPrice}
                      showChannelName={preferences.showChannelName}
                      showLocation={preferences.showLocation}
                      showCustomText={preferences.showCustomText}
                      renderer={labelRenderer}
                      boldAllFields={!!preferences.boldAllFields}
                    />
                  </SizedLabelBox>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ textTransform: 'none' }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintLabelDialog;
